import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BIOVIZ_STATS } from '@/config/env.config';
import { getAuth0Token } from '@/utils/auth0Token';
import urljoin from 'url-join';

// Custom baseQuery that handles CORS/Private Network Access errors gracefully
// Note: Browser console will still show CORS errors (they cannot be suppressed programmatically),
// but this ensures the error is handled gracefully in the application code
//
// CORS errors occur when:
// - Requesting from an origin not in the API's allowed origins list
// - Common in local development (localhost not in allowed origins)
// - This is a browser security feature, not an application error
//
// IBP 401/403 must NOT redirect to /securitycheck. Missing IBP project access (or a
// transient IBP auth failure) is not a dead portal session; treat as unavailable.
//
// IBP requests that never complete (seen on dev: the Bioinformatics tile spun forever)
// must surface as an error rather than an endless loading state.
export const BIOVIZ_REQUEST_TIMEOUT_MS = 30000;

// IBP's /stats/projects is paginated (default 30, hard cap 100 per page). Always ask for
// the cap; the page loop below follows meta.total_pages.
export const BIOVIZ_PROJECTS_PAGE_SIZE = 100;
// Safety valve so a bad meta.total_pages can never loop forever (100 x 100 = 10k projects).
export const BIOVIZ_PROJECTS_MAX_PAGES = 100;

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
	const baseQuery = fetchBaseQuery({ 
		baseUrl: urljoin(API_BIOVIZ_STATS),
		timeout: BIOVIZ_REQUEST_TIMEOUT_MS,
		prepareHeaders: async (headers) => {
			// Use Auth0 SDK to get access token (consistent with other APIs)
			try {
				const token = await getAuth0Token();
				if (token) {
					headers.set('authorization', `Bearer ${token}`);
				} else {
					console.warn('⚠️ biovizApi: No token available, request may fail');
				}
			} catch (error) {
				console.error('❌ biovizApi: Error getting token:', error);
			}
			return headers;
		}
	});
	
	const result = await baseQuery(args, api, extraOptions);

	// If there's an error, check if it's a CORS/network error and handle it gracefully
	if (result.error) {
		const error = result.error;
		
		// Check if it's a CORS, network, or Private Network Access error
		// CORS errors occur when the browser blocks the request before it reaches the server
		// This is expected in local development if localhost is not in allowed origins
		const isCorsOrNetworkError = 
			error.status === 'FETCH_ERROR' ||
			error.status === 'CUSTOM_ERROR' ||
			error.status === 0 ||
			(error.error && typeof error.error === 'string' && (
				error.error.includes('CORS') ||
				error.error.includes('blocked') ||
				error.error.includes('address space') ||
				error.error.includes('ERR_FAILED') ||
				error.error.includes('Failed to fetch')
			));
		
		if (isCorsOrNetworkError) {
			console.warn('⚠️ biovizApi: Request failed (CORS/network/redirect error), treating as unavailable', {
				error: error.message || error.error,
				status: error.status
			});
			return {
				error: {
					status: 'TIMEOUT',
					error: 'Bioviz service unavailable',
				},
			};
		}
		
		// For any other error (including 4xx/5xx), treat as unavailable - do not kill the portal session
		console.warn('⚠️ biovizApi: Request failed, treating as unavailable', {
			error: error.message || error.error,
			status: error.status
		});
		return {
			error: {
				status: 'TIMEOUT',
				error: 'Bioviz service unavailable',
			},
		};
	}
	
	return result;
};

const toPositiveInt = (value, fallback) => {
	const parsed = Number.parseInt(value, 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Walk every page of IBP GET /stats/projects and return the union of the pages.
 *
 * IBP paginates this list (issue NC-Team-Issues#1501: the tile only showed the first page).
 * Page 1 is requested with the maximum page size, then meta.total_pages decides how many
 * more pages to pull. If a later page fails, the projects collected so far are returned
 * with `meta.incomplete = true` rather than throwing everything away.
 *
 * @param {(args: object) => Promise<{data?: object, error?: object}>} fetchPage
 *   runs one request against the bioviz base query (the bound `baseQuery` RTK Query hands to `queryFn`)
 * @param {{ pageSize?: number, maxPages?: number, includeSampleSets?: boolean }} [options]
 * @returns {Promise<{ data?: { projects: object[], meta: object }, error?: object }>}
 */
export const fetchAllBiovizProjectPages = async (fetchPage, options = {}) => {
	const pageSize = options.pageSize || BIOVIZ_PROJECTS_PAGE_SIZE;
	const maxPages = options.maxPages || BIOVIZ_PROJECTS_MAX_PAGES;
	const projects = [];
	const seenIds = new Set();
	let totalPages = 1;
	let totalProjects = 0;
	let page = 1;

	const addProjects = (pageProjects) => {
		for (const project of pageProjects) {
			// Guard against a project shifting between pages while we walk them.
			const key = project?.project_id;
			if (key !== undefined && key !== null) {
				if (seenIds.has(key)) continue;
				seenIds.add(key);
			}
			projects.push(project);
		}
	};

	while (page <= totalPages && page <= maxPages) {
		const params = { per_page: pageSize, page };
		if (options.includeSampleSets) params.include_sample_sets = 'true';

		const result = await fetchPage({ url: 'projects', method: 'GET', params });

		if (result.error) {
			if (page === 1) return { error: result.error };
			return {
				data: {
					projects,
					meta: {
						total_projects: totalProjects,
						total_pages: totalPages,
						pages_fetched: page - 1,
						incomplete: true,
						error: result.error,
					},
				},
			};
		}

		const body = result.data || {};
		addProjects(Array.isArray(body.projects) ? body.projects : []);

		const meta = body.meta || {};
		if (page === 1) {
			totalPages = toPositiveInt(meta.total_pages, 1);
			totalProjects = toPositiveInt(meta.total_projects, projects.length);
		}
		if (meta.has_next_page === false) {
			totalPages = page;
		}
		page += 1;
	}

	return {
		data: {
			projects,
			meta: {
				total_projects: totalProjects || projects.length,
				total_pages: totalPages,
				pages_fetched: page - 1,
				incomplete: totalPages > maxPages,
			},
		},
	};
};

export const biovizApi = createApi({
	reducerPath: 'biovizApi',
	baseQuery: baseQueryWithErrorHandling,
	tagTypes: ['data'],
	endpoints: (builder) => ({
		fetchBiovizSampleSets: builder.query({
			query: (projectId) => ({
				// baseQuery prepareHeaders handles authorization header automatically
				url: `projects?project_id=${projectId}`,
				method: 'GET'
			}),
			// Cache data for 60 seconds (1 minute) after it's no longer used
			keepUnusedDataFor: 60,
			// Refetch if data is older than 60 seconds (1 minute TTL)
			// This ensures data is refreshed after 1 minute regardless of usage
			refetchOnMountOrArgChange: 60
		}),
		fetchAllBiovizProjects: builder.query({
			// Every page of /stats/projects, not just the first one (NC-Team-Issues#1501).
			// baseQuery prepareHeaders handles authorization header automatically.
			queryFn: (_arg, _api, _extraOptions, baseQuery) => fetchAllBiovizProjectPages(baseQuery),
			// Cache data for 5 minutes after it's no longer used
			keepUnusedDataFor: 300,
			// Refetch if data is older than 5 minutes
			refetchOnMountOrArgChange: 300
		})
	}),
});

export const {
	useFetchBiovizSampleSetsQuery,
	useFetchAllBiovizProjectsQuery,
} = biovizApi;